/**
 * SSOT for every BRAND IMAGE the product renders -- logo mark, wordmark, favicon, OG default, the property sentinel,
 * the PWA manifest icons and the closing-document logo -- keyed by brand slug so a white-label swap re-skins imagery
 * the same way `ACTIVE_BRAND` re-skins strings.
 *
 * WHY THIS LEAF EXISTS. Before it, semantic-asset indirection was two names (`FEDERA_LOGO`, `SENTINEL_PROPERTY_IMAGE`)
 * against roughly two dozen hand-spelled asset paths across index.html templates, Next metadata blocks, a PWA
 * manifest, a sitemap thumbnail default and four favicon copies -- and one of those spellings
 * (`/assets/placeholder-property.jpg`, the video sitemap's thumbnail) named a file that has never existed in this
 * repo. A path spelled once in a registry cannot 404 silently in a surface nobody renders by hand.
 *
 * DELIBERATELY NOT ON `BrandIdentity` (`const/brand.ts`). That record is the brand's STRING identity and is owned by
 * its own wave; imagery carries different fields and different consumers, so the two stay separate records -- but the
 * SELECTION between them is one decision and is made once, in `const/brand-registry.ts`. {@link ACTIVE_BRAND_ASSETS}
 * RESOLVES through `ACTIVE_BRAND_NAME`; it does not mirror it with a key of its own. A mirror is two lines that can
 * disagree, and this one did: flipping the selector re-skinned every string and left ALL imagery on the incumbent
 * brand, which is precisely the invisible fallback the `Record<BrandName, ...>` totality exists to prevent.
 *
 * RENDERED COPY IS COMPOSED OFF THE MANIFEST'S OWN BRAND, NEVER OFF THE SELECTION. `alt`, `wordmark.text` and
 * `wordmark.ariaLabel` are user-facing strings, so {@link FEDERA_BRAND_ASSETS} -- the manifest registered for the brand
 * NAMED federa -- reads them off `FEDERA_BRAND.name` (`const/brand.ts`), the identity record of the brand it IS. Two
 * failures are being avoided at once, and they pull in opposite directions. A hand-typed name would survive a
 * white-label swap and leave the wordmark reading the previous company's name
 * (`DS-BRAND-LITERAL-OUTSIDE-RECORD-001` governs that class tree-wide). Reading the SELECTION (`ACTIVE_BRAND`) would be
 * the mirror defect and was live until this leaf was corrected: register a second brand, flip `ACTIVE_BRAND_NAME`, and
 * `BRAND_ASSETS.federa.wordmark.text` renders the OTHER brand's name inside federa's own manifest. It is the same
 * inversion `brand/federa/tokens/brand-manifest.ts` records refusing for its `brandName`. The selection is made ONCE,
 * at {@link ACTIVE_BRAND_ASSETS}, by choosing WHICH manifest -- never by what a named manifest says about itself.
 *
 * INTRINSIC DIMENSIONS ARE MEASURED, NEVER GUESSED. The in-repo entries carry the pixel sizes read out of the files
 * themselves; the CDN entry carries `null` because its bytes are not in this repo and the account serves only the
 * `public` (original) variant, so nothing here could measure it.
 */

import { ACTIVE_BRAND_NAME, BRAND_NAMES } from './brand-registry.ts';
import { FEDERA_BRAND } from './brand.ts';
import { CLOUDFLARE_IMAGES_DELIVERY_BASE } from './urls-connect.ts';
import type { BrandAssetManifest } from '@federa/types/brand-assets';

/** The brand key set, DERIVED from the owning name record in one line rather than mirrored into a key record of this
 *  leaf's own -- the same derivation `const/brand-registry.ts` and `tools/design-tokens/generate-css-vars.ts` make. A
 *  mirror is a second declaration of which brands exist: a brand added to `BRAND_NAMES` and forgotten here would not be
 *  a compile error at {@link BRAND_ASSETS} at all, it would surface one leaf away as an `undefined` manifest, and only
 *  for whichever brand happened to be selected. Derived, the `Record<BrandName, ...>` annotation below makes a
 *  registered brand without imagery a compile error on the spot. */
type BrandName = (typeof BRAND_NAMES)[keyof typeof BRAND_NAMES];

/**
 * The canonical favicon source. Next's App Router resolves `src/app/favicon.ico` by FILE CONVENTION and Vite copies
 * only from a project root's own `public/`, so neither tool can read a shared file elsewhere in the tree -- the other
 * entries in `faviconCopies` are byte-identical copies this path owns, asserted by
 * `brand/federa/assets/__tests__/images.test.ts`.
 */
const FEDERA_FAVICON_SRC = 'apps/federa/src/app/favicon.ico';

/**
 * The FEDERA brand mark. A Cloudflare Images delivery url, not a repo file: it is rendered by services with no shared
 * origin (the admin app, the profile SPA, the auth modal in its own worker), and an origin-relative path would resolve
 * against whichever origin happened to serve the page.
 */
const FEDERA_LOGO_MARK_SRC = `${CLOUDFLARE_IMAGES_DELIVERY_BASE}/88a64a5b-b1ef-41b1-5fd2-a4a0b42fed00/public`;

/**
 * Sentinel image for properties without images (indicates "no data") -- a LOCAL asset, deliberately not a CDN one.
 *
 * It used to be a `${CLOUDFLARE_IMAGES_DELIVERY_BASE}` url: the account has only the `public` named variant and
 * flexible variants are disabled (`ERROR 9429`), so `public` served the 4096x2730 ORIGINAL -- 1.81 MB for a
 * placeholder rendered at 884x457 at its very largest. This is the most-shown image in the product (explore feed and
 * grid, map, wishlists, navigator, chats, trade, the property gallery), and on the property page it WAS the Largest
 * Contentful Paint element: 1,456,829 transfer bytes, 53% of the entire page. The local file is the same artwork at
 * 1400x933 WebP, quality 68 -- 80,984 bytes.
 *
 * Root-relative on purpose: every consuming surface (`/__mfe/*`, `/explore`, the app's own routes) is served from this
 * origin, and `apps/federa/public/_headers` already gives `/*.webp` a one-year immutable cache. Reaching it from an
 * Open Graph or `ImageResponse` context needs an ABSOLUTE url, so those callers join it onto a resolved site origin
 * (`getPlaceholderImageUrl`) rather than emitting the bare path.
 */
const FEDERA_SENTINEL_PROPERTY_IMAGE_SRC = '/property-photo-coming-soon.webp';

const FEDERA_BRAND_ASSETS: BrandAssetManifest = {
	logoMark: {
		src: FEDERA_LOGO_MARK_SRC,
		// Not in this repo and served only as the account's `public` (original) variant -- nothing here can measure it.
		width: null,
		height: null,
		alt: FEDERA_BRAND.name,
		delivery: 'cdn',
	},
	wordmark: {
		text: FEDERA_BRAND.name,
		ariaLabel: FEDERA_BRAND.name,
	},
	favicon: {
		// The SERVED url, identical on every origin. The repo paths holding its bytes are `faviconCopies`.
		src: '/favicon.ico',
		// A 3-entry .ico (16/32/48 square); the largest entry is the intrinsic size recorded here.
		width: 48,
		height: 48,
		alt: '',
		delivery: 'build-copied',
	},
	ogDefault: {
		// The OG default and the schema.org Organization `logo` are the SAME image as the mark today. Recorded as its
		// own slot rather than aliased, because an OG card wants a 1200x630 landscape crop and this is a square mark --
		// a dedicated OG asset is the eventual fix, and it lands by changing this entry alone.
		src: FEDERA_LOGO_MARK_SRC,
		width: null,
		height: null,
		alt: FEDERA_BRAND.name,
		delivery: 'cdn',
	},
	sentinelPropertyImage: {
		src: FEDERA_SENTINEL_PROPERTY_IMAGE_SRC,
		width: 1400,
		height: 933,
		alt: '',
		delivery: 'origin-relative',
	},
	manifestIcons: [
		{ src: '/icon-192.png', width: 192, height: 192, alt: FEDERA_BRAND.name, delivery: 'origin-relative' },
		{ src: '/icon-512.png', width: 512, height: 512, alt: FEDERA_BRAND.name, delivery: 'origin-relative' },
	],
	closingDocLogo: {
		// PROVENANCE IS UNRECOVERED. The bytes live as a base64 literal in the module named here because the PDF
		// generator runs with no runtime fetch; the PNG they were produced from is NOT in this repo and its recorded
		// former path (`brand/federa/logos/Federa-Gradient-Icon.png`) resolves to nothing -- see that module's
		// docstring. `src` therefore names the OWNING MODULE, which is the only place the bytes exist.
		src: 'utils/documents/closing-doc-logo-asset.ts',
		width: 335,
		height: 349,
		alt: FEDERA_BRAND.name,
		delivery: 'inline-base64',
	},
	faviconCopies: [
		FEDERA_FAVICON_SRC,
		// Next App Router file convention -- must sit at this exact path in each app.
		'apps/admin-federa/src/app/favicon.ico',
		// Vite `publicDir` of each standalone SPA shell whose index.html links `/favicon.ico`; the worker's [assets]
		// block serves the build output, so a shell with no `public/favicon.ico` 404s on its own icon link.
		'spa/actions/public/favicon.ico',
		'spa/analytics/public/favicon.ico',
		'spa/connect/public/favicon.ico',
		'spa/explore/public/favicon.ico',
		'spa/groups/public/favicon.ico',
		'spa/market-maker/public/favicon.ico',
		'spa/profile/public/favicon.ico',
		// The navigator MFE's standalone shell: its vite root is `mfe/navigator/frontend`, so its publicDir is there.
		'mfe/navigator/frontend/public/favicon.ico',
	],
};

/** Every brand's imagery manifest, keyed by the name record (`BRAND_NAMES`, `const/brand-registry.ts`). The
 *  `Record<BrandName, ...>` totality is the load-bearing half: a brand registered there with no manifest here is a
 *  compile error at THIS declaration, not an `undefined` asset discovered at a renderer. */
export const BRAND_ASSETS = {
	[BRAND_NAMES.FEDERA]: FEDERA_BRAND_ASSETS,
} as const satisfies Readonly<Record<BrandName, BrandAssetManifest>>;

/**
 * The active brand's imagery. RESOLVED from {@link ACTIVE_BRAND_NAME} (`const/brand-registry.ts`), never assigned a
 * manifest directly -- an assignment would make the white-label switch two lines that can disagree, which is exactly
 * how imagery stayed on the incumbent brand while every string re-skinned. THIS is the only line in the leaf that reads
 * the selection; a brand registered in `BRAND_NAMES` with no manifest above is a COMPILE error at {@link BRAND_ASSETS}
 * rather than an `undefined` asset discovered at a renderer.
 */
export const ACTIVE_BRAND_ASSETS: BrandAssetManifest = BRAND_ASSETS[ACTIVE_BRAND_NAME];
